import React, {useState} from 'react';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreateProteinForm, {
  getCreateFormValidation,
  isRealTranslationLink,
  TranslationLinkFields,
  TranslationLinkOrDummy,
  UNSELECTED_DUMMY_OBJECT,
} from 'molbio/proteins/CreateProteinForm';
import {BioentityLinkFieldWithTagSchema} from 'schemas/fields/SchemaFieldConstants';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

export interface ConfirmCreateProteinPayload {
  name: string;
  folderId: string;
  translationLink?: BioentityLinkFieldWithTagSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}

export interface CreateProteinModalProps {
  initialFolderId?: string;
  namePlaceholder?: string;
  onRequestSubmit: (result: ConfirmCreateProteinPayload) => void;
  onRequestClose: () => void;
  translationLinkFields?: TranslationLinkFields;
}

export default function CreateProteinModal({
  initialFolderId,
  namePlaceholder,
  onRequestSubmit,
  onRequestClose,
  translationLinkFields = [],
}: CreateProteinModalProps): JSX.Element {
  const [name, setName] = useState(namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(null);
  const [translationLink, setTranslationLink] = useState<TranslationLinkOrDummy>(UNSELECTED_DUMMY_OBJECT);
  const [selectedStudyItems, setSelectedStudyItems] = useState<Array<StudySearchItem>>([]);

  const {isValid} = getCreateFormValidation({
    name,
    folderId,
    translationLink,
    translationLinkFields,
    tagSchemaId,
  });

  const handleConfirm = (): void => {
    const translationLinkValue: BioentityLinkFieldWithTagSchema | null = !isRealTranslationLink(
      translationLink
    )
      ? null
      : translationLink;
    const confirmPayload: ConfirmCreateProteinPayload = {
      folderId: folderId!,
      name,
      translationLink: translationLinkValue,
    };
    if (translationLinkValue == null && tagSchemaId != null) {
      confirmPayload.tagSchemaId = tagSchemaId;
    }
    if (selectedStudyItems.length > 0) {
      confirmPayload.studyIds = selectedStudyItems.map((studyItem) => studyItem.id);
    }
    onRequestSubmit(confirmPayload);
    onRequestClose();
  };

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Create',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
      onClick: handleConfirm,
    },
  ];

  return (
    <Modal
      title="Create AA sequence"
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestClose={onRequestClose}
      buttons={buttons}
    >
      <CreateProteinForm
        name={name}
        folderId={folderId}
        tagSchemaId={tagSchemaId}
        translationLink={translationLink}
        selectedStudyItems={selectedStudyItems}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onTagSchemaIdChange={setTagSchemaId}
        onTranslationLinkChange={setTranslationLink}
        onSelectedStudyItemsChange={setSelectedStudyItems}
        translationLinkFields={translationLinkFields}
      />
    </Modal>
  );
}
